import React, { type ReactNode } from "react";
import { type GetStaticProps, type NextPage } from "next";
import { Text, SimpleGrid, Flex, AspectRatio, type SpaceProps } from "@chakra-ui/react";
import Balancer from "react-wrap-balancer";
import Image from "next/image";
import {
  getPage,
  getAnnouncementBanner,
  getStackedLogos,
  getSimpleIntegrations,
  type Page as SanityPage,
  type IntegrationSimple,
  type Banner,
  type Customer,
} from "lib/sanity";
import { Page } from "components/page";
import { FeatureOneIcon, FeatureTwoIcon, FeatureThreeIcon } from "components/platform/event-collection/icons";
import { HalfHeroAnimation, HeroVideo } from "components/half-hero-animation";
import { StackedLogos } from "components/logos";
import { Section, sectionSpacingY } from "components/section";
import DestinationMarquee from "components/destination-marquee";
import { QuoteCard } from "components/quotes";
import { IconFeature } from "components/icon-feature";
import { SectionHeading } from "components/section-heading";
import { PairImage } from "components/pair-image";
import { GradientBorder } from "ui/gradient-border";
import { RadialGradient } from "ui/radial-gradient";
import { getPageMetaImage } from "utils";
import { integrationsCount } from "../../constants/integrations";
import pairOneImage from "../../../public/assets/platform/event-collection/pair-1.svg";
import pairTwoImage from "../../../public/assets/platform/event-collection/pair-2.svg";
import pairThreeImage from "../../../public/assets/platform/event-collection/pair-3.svg";
import fullWidthImage from "../../../public/assets/platform/event-collection/full-width.svg";
import pairFourImage from "../../../public/assets/platform/event-collection/pair-4.svg";
import pairFiveImage from "../../../public/assets/platform/event-collection/pair-5.svg";

const featureGradientWeb =
  "linear-gradient(to bottom, transparent 0%, var(--chakra-colors-mint) 34.38%, var(--chakra-colors-cyan) 64.06%, transparent 100%)";
const featureGradientMobile =
  "linear-gradient(to right, transparent 0%, var(--chakra-colors-mint) 34.38%, var(--chakra-colors-cyan) 64.06%, transparent 100%)";

const featureStyles = {
  pr: { base: 0, md: 8, lg: 12 },
  pb: { base: 12, md: 0 },
  pos: "relative",
  _before: {
    content: "''",
    bg: { base: featureGradientMobile, md: featureGradientWeb },
    pos: "absolute",
    right: 0,
    bottom: 0,
    top: { base: "auto", md: 0 },
    left: { base: 0, md: "auto" },
    width: { base: "100%", md: "2px" },
    height: { base: "2px", md: "100%" },
  },
};

function DarkSection({ children, ...props }: { children: ReactNode } & SpaceProps) {
  return (
    <Section theme="dark" themeBgColor="base.darkBackground" bg="base.darkBackground" color="white" {...props}>
      {children}
    </Section>
  );
}

type PageProps = {
  page: SanityPage;
  announcementBanner?: Banner;
  destinations: IntegrationSimple[];
  customerRows: Customer[][];
};

const EventCollection: NextPage<Readonly<PageProps>> = ({ page, announcementBanner, destinations, customerRows }) => {
  return (
    <Page
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      announcementBanner={announcementBanner}
      theme="dark"
      bgColor="base.darkBackground"
    >
      <HalfHeroAnimation
        hideSecondary
        eyebrow={page.eyebrow}
        title={page.title}
        description={page.description}
        theme="dark"
        themeBgColor="base.darkBackground"
        bg="base.darkBackground"
        color="white"
        my={0}
        py={{ base: 12, md: 16, lg: 24 }}
        backgroundGradient={
          <>
            <RadialGradient variant="lightspeed" top="-32%" left="-74%" w="100%" h="60%" opacity={0.6} />
            <RadialGradient variant="mint" top="-50%" left="-40%" w="88%" h="70%" opacity={0.8} />
          </>
        }
        animationElement={
          <HeroVideo
            isTransparent
            title="Hightouch Events"
            description={page.metaDescription}
            uploadDate="2023-10-03T08:00:00+08:00"
            sourceMp4="https://cdn.sanity.io/files/pwmfmi47/production/f0953ef6f95cce1917023b5568c5d19ef50cc856.mp4"
            sourceWebm="https://cdn.sanity.io/files/pwmfmi47/production/7edff46a1487f4d43f6f43df414402fc9c9a4c3d.webm"
            poster="https://cdn.sanity.io/images/pwmfmi47/production/ba092f9cf82a272bee636210ffef8bfef48be10d-2100x1608.webp"
            ml={{ base: undefined, xl: -10 }}
            maxW={{ base: undefined, xl: "fit-content" }}
          />
        }
      />

      <StackedLogos
        title=""
        customerRows={customerRows}
        theme="dark"
        themeBgColor="base.darkBackground"
        bg="base.darkBackground"
        pt={{ base: 4, md: 6, xl: 12 }}
      />

      <DarkSection py={0}>
        <SimpleGrid
          as="ul"
          columns={{ base: 1, md: 3 }}
          gap={{ base: 12, md: 8, lg: 16 }}
          maxW={{ base: "md", md: "100%" }}
          mx="auto"
        >
          <IconFeature
            as="li"
            icon={FeatureOneIcon}
            iconColor="mint"
            eyebrow="Step 1"
            title="Implement Hightouch’s SDK"
            description="Add simple tracking code to your website, mobile app, or server-side application to track any events you like."
            sx={{ ...featureStyles }}
          />

          <IconFeature
            as="li"
            icon={FeatureTwoIcon}
            iconColor="mint"
            eyebrow="Step 2"
            title="Stream data to your warehouse"
            description="Deliver clean, reliable event data to your data warehouse with full flexibility for any schema or use case."
            sx={{ ...featureStyles }}
          />

          <IconFeature
            as="li"
            icon={FeatureThreeIcon}
            iconColor="mint"
            eyebrow="Step 3"
            title="Activate data across your tools"
            description="Put event data to work in the marketing, advertising, analytics, and operational tools you use every day."
          />
        </SimpleGrid>
      </DarkSection>

      <DarkSection>
        <SectionHeading title="Collect higher quality data — faster" textAlign="center" mb={{ base: 16, lg: 24 }} />

        <Flex direction="column" gap={{ base: 16, lg: 24 }} w="100%">
          <PairImage
            align="right"
            title="Easily collect events into the warehouse"
            description="Implementing Hightouch’s SDK into your website or app is simple. Plus, we’re backwards compatible with other major event collection providers, so it’s even easier to migrate."
            actionText="Read our Browser SDK docs"
            actionUrl="/docs/events/sdks/browser"
            linkVariant="mint"
            image={pairOneImage}
            imageRatio={3 / 2}
            backgroundGradient={
              <>
                <RadialGradient variant="cyan" boxSize="70%" top={10} right={-4} />
                <RadialGradient variant="mint" w="90%" h="75%" bottom={0} right={-2} />
              </>
            }
          />

          <PairImage
            align="right"
            title="Ensure high quality data"
            description="Define data contracts to proactively govern your data and ensure every event meets your expectations before being written to the data warehouse."
            actionText="Read about data contracts"
            actionUrl="/blog/data-contracts-hightouch-events"
            linkVariant="mint"
            image={pairTwoImage}
            imageRatio={3 / 2}
            backgroundGradient={
              <>
                <RadialGradient variant="cyan" boxSize="60%" bottom={6} right="12%" />
                <RadialGradient variant="mint" boxSize="70%" top={12} left={0} />
              </>
            }
          />

          <PairImage
            align="right"
            title="Use event data across your business"
            description={`Sync event data from your data warehouse to ${integrationsCount}+ tools. Power your operations, marketing, advertising, and more with events you collect.`}
            actionText="See all integrations"
            actionUrl="/integrations"
            linkVariant="mint"
            image={pairThreeImage}
            backgroundGradient={
              <>
                <RadialGradient variant="cyan" boxSize="30%" left={16} top={16} />
                <RadialGradient variant="mint" boxSize="50%" left="16%" top="10%" />
              </>
            }
          />
        </Flex>
      </DarkSection>

      <DarkSection py={0}>
        <GradientBorder bg="base.darkBackground" pt={{ base: 8, md: 12, lg: 20 }}>
          <SectionHeading
            eyebrowColor="mint"
            title="Real-time event streaming"
            description="Sync event data to your downstream tools, where and when you need it."
            textAlign="center"
            w="100%"
            px={6}
          />
          <AspectRatio ratio={4 / 1} pos="relative" mt={{ base: 12, lg: 16 }} w="100%">
            <Image
              fill
              alt="Real-time events streaming to downstream tools."
              src={fullWidthImage}
              sizes="100%"
              style={{ objectFit: "contain" }}
            />
          </AspectRatio>
        </GradientBorder>
      </DarkSection>

      <DarkSection>
        <SectionHeading title="Have full control of your workflows" textAlign="center" mb={{ base: 16, lg: 24 }} />

        <Flex direction="column" gap={{ base: 16, lg: 24 }}>
          <PairImage
            align="left"
            title="Manage changes with version control"
            description="Git sync management makes it easy to manage your tracking plan alongside your code so that you can keep up to date and catch regressions before they reach production."
            image={pairFourImage}
            backgroundGradient={
              <>
                <RadialGradient variant="cyan" boxSize="70%" left={0} bottom={8} />
                <RadialGradient variant="mint" boxSize="50%" right={0} top={0} />
              </>
            }
          />

          <PairImage
            align="left"
            title="Observe and troubleshoot your events"
            description="Get deep event-level observability and customizable alerting for swift issue diagnosis so you can triage quickly."
            image={pairFiveImage}
            backgroundGradient={
              <>
                <RadialGradient variant="cyan" boxSize="60%" top="16%" right={12} />
                <RadialGradient variant="mint" boxSize="75%" left={4} bottom={4} />
              </>
            }
          />
        </Flex>
      </DarkSection>

      <Section center bg="white">
        <Text as="h2" textStyle="h4" textAlign="center" maxW="3xl">
          <Balancer>See how leading teams activate their data</Balancer>
        </Text>
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={5} mt={{ base: 8, md: 12, lg: 16 }}>
          <QuoteCard
            hasBorder
            bg="gorgias"
            logo="https://cdn.sanity.io/images/pwmfmi47/production/e62fd72fdffaa05f40e8251f137d416fad604a64-302x72.svg"
            quote="Hightouch allows us to target the same people and push audiences to different channels so the same people see the same ads across the Internet."
            author="Max Sutra"
            job="Growth and Demand Gen Lead"
            company="Gorgias"
            href="/customers/gorgias"
          />

          <QuoteCard
            hasBorder
            bg="lucidchart"
            logo="https://cdn.sanity.io/images/pwmfmi47/production/a2cefd7b22e1d57caba02e8a951242756d4017b8-609x164.png"
            quote="With Hightouch, we can trust that the same data that lives in our internal reports is the same data that is getting passed to our ad platforms."
            author="Mallory Reese-Bagley"
            job="Senior Marketing Operations Manager"
            company="Lucid"
            href="/customers/lucidchart"
          />
          <QuoteCard
            hasBorder
            bg="intelycare"
            logo="https://cdn.sanity.io/images/pwmfmi47/production/76de215885530de72468b8856ca805003de937f3-240x50.svg"
            quote="Our Hightouch users are able to build audiences powering emails, texts, and in-app messages – all without ever having to interact with the data team."
            author="Ben Tenglesen"
            job="VP of Data Science"
            company="IntelyCare"
            href="/customers/intelycare"
          />
        </SimpleGrid>
      </Section>

      <DestinationMarquee
        heading={`Sync data from the warehouse to ${integrationsCount}+ tools`}
        destinations={destinations}
        my={0}
        pb={sectionSpacingY}
      />
    </Page>
  );
};

export default EventCollection;

export const getStaticProps: GetStaticProps<PageProps> = async () => {
  const [page, destinations, announcementBanner, customerRows] = await Promise.all([
    getPage("/platform/event-collection"),
    getSimpleIntegrations("destination"),
    getAnnouncementBanner(),
    getStackedLogos(),
  ]);

  if (!page) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      page,
      destinations,
      announcementBanner,
      customerRows,
    },
    revalidate: 60,
  };
};
